import { useEffect, useState } from 'react'
import { deadlineFetch } from './customEndpointFetch'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

/**
 * A prior pet profile that looks like the same animal as the one being viewed.
 * Surfaced so a vet can copy alert thresholds forward instead of re-entering
 * them every time a new sensor spins up a fresh pet record. See
 * ada-core `Pets/possiblePredecessors.ts` for the matching rules.
 */
export interface PredecessorPet {
  id: number
  name: string
  species: string | null
  breed: string | null
  srcid: number | null
  status: number | null
  isMerged: boolean
  deviceCount: number
  alertRuleCount: number
  hasAlertRules: boolean
  lastSensorActivity: number | null
}

/**
 * Fetches the owner's other pets that look like the same animal as `petId`.
 *
 * Mirrors useDevicesWithMetrics' resilience contract: uses the `JWT` auth
 * prefix, never clears the session on failure, and degrades to an empty list
 * (never throws) so a flaky call just hides the "copy thresholds" affordance
 * rather than breaking the alert-rules editor. Returns [] until a real petId
 * is supplied.
 */
export const usePossiblePredecessors = (
  petId: number | undefined | null,
): { predecessors: PredecessorPet[]; isLoading: boolean } => {
  const [predecessors, setPredecessors] = useState<PredecessorPet[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!petId) {
      setPredecessors([])
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    let cancelled = false
    setIsLoading(true)

    // Auth via cookie OR token — NOT token-only. On a browser reopen /
    // new tab sessionStorage is empty but the HttpOnly payload-token cookie is
    // still valid, so the same-origin request authenticates via the cookie;
    // attach the JWT header only as a bonus when a token happens to be present.
    // Bailing on a missing token silently hid this affordance on every reopen
    // (L-019, sibling of the a995249f blank-charts fix).
    const token = sessionStorage.getItem('token')
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers.Authorization = `JWT ${token}`

    deadlineFetch(`${apiUrl}/pets/${petId}/possible-predecessors`, {
      headers,
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          setPredecessors([])
          return
        }
        const data = await res.json()
        if (cancelled) return
        setPredecessors(Array.isArray(data?.predecessors) ? data.predecessors : [])
      })
      .catch(() => {
        if (cancelled) return
        setPredecessors([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [petId])

  return { predecessors, isLoading }
}
